import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../../services/api';
import {
  TeleconsultManagementDashboard,
  TeleconsultManagementTypeCount,
  TeleconsultManagementVisit,
} from '../../types/api';
import { getApiErrorMessage } from '../../utils/apiError';
import { getTodayISO, TELECONSULT_QUEUE_TIME_ZONE } from '../../utils/date';
import {
  formatDurationCompact,
  formatManagementClock,
  formatManagementDay,
  formatSignedMinutes,
  median,
  secondsBetween,
  secondsBetweenRegisterAndStripePay,
} from './formatManagementTime';
import ManagementDateRangePicker from './ManagementDateRangePicker';
import {
  MAX_RANGE_DAYS,
  activePreset,
  addDaysIso,
  applyPresetRange,
  DatePreset,
  inclusiveDaySpan,
  parseIso,
  previousComparisonRange,
  previousRangeCaption,
} from './managementRange';
import ChatTranscriptDialog from './ChatTranscriptDialog';
import MultiCheckDropdown from './MultiCheckDropdown';
import {
  REASON_PRESET_OPTIONS,
  distinctDocumentLabels,
  distinctStaffNames,
  filterAndSortLogVisits,
  hasActiveLogFilters,
  type LogFilterState,
  type ReasonPreset,
} from './logFilters';
import {
  VISIT_CANCEL_REASON_OTHER,
  isVisitCancelReasonValid,
} from './cancelReasons';
import { VisitCancelReasonPicker } from './VisitCancelReasonPicker';
import { buildJourneyHops, idVerificationSampleSeconds, staffHandlingSampleSeconds } from './visitJourney';
import {
  COMPLETED_BAR_COLOR,
  buildVisitChartSeries,
  isDenseDailyChart,
  shouldShowVisitChartAxisLabel,
  visitChartAxisLabel,
  visitChartSubtitle,
  type VisitChartBucket,
} from './visitsChart';

type ManagementSubTab = 'dashboard' | 'log';
type ActionKind = 'cancel' | 'refund';
type ReasonPopup = 'cancel' | 'refund' | 'completed' | null;

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

const TYPE_COLORS: Record<string, string> = {
  CSGF: '#f97316',
  Stripe: '#3b82f6',
  SSD: '#14b8a6',
  TEST: '#eab308',
  Other: '#94a3b8',
};

const KPI = {
  pay: 5 * 60,
  id: 10 * 60,
  start: 15 * 60,
  consult: 15 * 60,
  checkout: 5 * 60,
  e2e: 35 * 60,
};

const PRESETS: { id: DatePreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7 days' },
  { id: 'month', label: 'This month' },
  { id: 'year', label: 'This year' },
];

function typeColor(type: string): string {
  return TYPE_COLORS[type] || '#64748b';
}

function statusLabel(status: string): string {
  if (status === 'completed') return 'Completed';
  if (status === 'cancelled') return 'Cancelled';
  if (status === 'refunded') return 'Refunded';
  if (status === 'open') return 'Open';
  return status;
}

function formatSgd(n?: number | null, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `SGD ${n.toLocaleString('en-SG', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

const REFUND_RETAIN_SGD = 2;

type RefundMode = 'full' | 'retain2' | 'custom';

function visitRefundedSgd(r: TeleconsultManagementVisit): number {
  if (r.stripeRefundedSgd != null && Number.isFinite(r.stripeRefundedSgd) && r.stripeRefundedSgd > 0) {
    return r.stripeRefundedSgd;
  }
  if (r.refunded && r.stripeAmountSgd) return r.stripeAmountSgd;
  return 0;
}

function visitRemainingSgd(r: TeleconsultManagementVisit): number {
  const charged = r.stripeAmountSgd ?? 0;
  const left = Math.round((charged - visitRefundedSgd(r)) * 100) / 100;
  return left >= 0.01 ? left : 0;
}

function refundPreviewSgd(remaining: number, mode: RefundMode, customRaw: string): number | null {
  if (remaining < 0.01) return null;
  if (mode === 'full') return remaining;
  if (mode === 'retain2') {
    const refund = Math.round((remaining - REFUND_RETAIN_SGD) * 100) / 100;
    return refund >= 0.01 ? refund : null;
  }
  const custom = Number.parseFloat(customRaw);
  if (!Number.isFinite(custom)) return null;
  const rounded = Math.round(custom * 100) / 100;
  if (rounded < 0.01 || rounded > remaining) return null;
  return rounded;
}

function visitDayIso(v: TeleconsultManagementVisit): string {
  return String(v.visitDate || '').slice(0, 10);
}

function doctorKey(v: TeleconsultManagementVisit): string {
  return (v.doctorName || '').trim() || 'Unassigned';
}

function staffKey(v: TeleconsultManagementVisit): string {
  return (v.registrationStaffName || '').trim() || 'System';
}

function staffStepLabel(v: TeleconsultManagementVisit): string {
  return (v.handlingStaffStep || '').trim() || (staffKey(v) === 'System' ? 'Fully automatic' : 'Ops');
}

function formatStaffStepMix(rows: TeleconsultManagementVisit[]): string {
  const counts = new Map<string, number>();
  rows.forEach((v) => {
    const step = staffStepLabel(v);
    counts.set(step, (counts.get(step) || 0) + 1);
  });
  const parts = Array.from(counts.entries()).sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  if (parts.length === 1) return parts[0][0];
  return parts.map(([step, n]) => `${step} ${n}`).join(' · ');
}

function isNonStripe(type: string): boolean {
  const t = type.toUpperCase();
  return t === 'CSGF' || t === 'SSD';
}

function uniquePatientKey(v: TeleconsultManagementVisit): string {
  return `${(v.patientName || '').trim().toLowerCase()}|${(v.maskedNric || '').trim()}`;
}

function nricLast4(masked: string | undefined): string {
  const s = (masked || '').replace(/\s/g, '');
  if (s.length <= 4) return s || '—';
  return s.slice(-4);
}

function pct(part: number, whole: number): string {
  if (whole <= 0) return '0%';
  return `${((part / whole) * 100).toFixed(1)}%`;
}

function emptyDashboard(): TeleconsultManagementDashboard {
  return {
    from: '',
    to: '',
    total: 0,
    completed: 0,
    cancelled: 0,
    refunded: 0,
    open: 0,
    uniquePatients: 0,
    newPatients: 0,
    openWaitingId: 0,
    openWaitingDoctor: 0,
    pendingJuliusCheckout: 0,
    stripeCollectedSgd: 0,
    stripeRefundedSgd: 0,
    stripeNetSgd: 0,
    avgTriageSampleCount: 0,
    avgRegisterToPickSampleCount: 0,
    avgPickToConsultEndSampleCount: 0,
    avgConsultEndToCheckoutSampleCount: 0,
    byType: [],
    byDoctor: [],
    cancelReasons: [],
    refundReasons: [],
  };
}

function summarizeVisits(rows: TeleconsultManagementVisit[]): TeleconsultManagementDashboard {
  const dash = emptyDashboard();
  dash.total = rows.length;
  dash.completed = rows.filter((r) => r.status === 'completed').length;
  dash.cancelled = rows.filter((r) => r.status === 'cancelled').length;
  dash.refunded = rows.filter((r) => r.status === 'refunded').length;
  dash.open = rows.filter((r) => r.status === 'open').length;
  dash.uniquePatients = new Set(rows.map(uniquePatientKey)).size;
  dash.newPatients = rows.filter((r) => r.newPt).length;
  let collected = 0;
  let refundedSgd = 0;
  rows.forEach((r) => {
    if (r.stripeAmountSgd == null) return;
    collected += r.stripeAmountSgd;
    refundedSgd += visitRefundedSgd(r);
  });
  dash.stripeCollectedSgd = collected;
  dash.stripeRefundedSgd = refundedSgd;
  dash.stripeNetSgd = collected - refundedSgd;
  const countMap = (keyFn: (r: TeleconsultManagementVisit) => string) => {
    const map = new Map<string, number>();
    rows.forEach((r) => {
      const k = keyFn(r) || '(unknown)';
      map.set(k, (map.get(k) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([type, count]) => ({ type, count }));
  };
  dash.byType = countMap((r) => r.type);
  dash.byDoctor = countMap((r) => (r.status === 'completed' ? doctorKey(r) : '')).filter((x) => x.type);
  dash.cancelReasons = countMap((r) =>
    r.status === 'cancelled' ? r.cancellationReason?.trim() || '(no reason recorded)' : '',
  ).filter((x) => x.type);
  dash.refundReasons = countMap((r) =>
    visitRefundedSgd(r) > 0 ? r.refundReason?.trim() || '(no reason recorded)' : '',
  ).filter((x) => x.type);
  return dash;
}

/** Right now card and patient log. Refreshed while that view is open. */
const MANAGEMENT_POLL_MS = 12_000;

function waitSeconds(fromIso?: string | null, asOf: Date = new Date()): number | null {
  if (!fromIso) return null;
  return secondsBetween(fromIso, asOf.toISOString());
}

export default function StaffManagementPanel() {
  const today = getTodayISO();
  const [subTab, setSubTab] = useState<ManagementSubTab>('dashboard');
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [staffFilter, setStaffFilter] = useState<string[]>([]);
  const [docsFilter, setDocsFilter] = useState<string[]>([]);
  const [reasonPreset, setReasonPreset] = useState<ReasonPreset>('any');
  const [chatVisit, setChatVisit] = useState<TeleconsultManagementVisit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState<TeleconsultManagementDashboard | null>(null);
  const [previous, setPrevious] = useState<TeleconsultManagementDashboard | null>(null);
  const [visits, setVisits] = useState<TeleconsultManagementVisit[]>([]);
  const [prevVisits, setPrevVisits] = useState<TeleconsultManagementVisit[]>([]);
  const [liveVisits, setLiveVisits] = useState<TeleconsultManagementVisit[]>([]);
  const [now, setNow] = useState(() => new Date());
  const [reasonPopup, setReasonPopup] = useState<ReasonPopup>(null);
  const [actionKind, setActionKind] = useState<ActionKind | null>(null);
  const [actionVisit, setActionVisit] = useState<TeleconsultManagementVisit | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [cancelReasonCode, setCancelReasonCode] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [reasonBusy, setReasonBusy] = useState(false);
  const [refundMode, setRefundMode] = useState<RefundMode>('full');
  const [customRefundSgd, setCustomRefundSgd] = useState('');

  const fullLoadInFlight = useRef(false);
  const preset = activePreset(from, to, today);
  const prevRange = useMemo(() => previousComparisonRange(from, to, preset), [from, to, preset]);
  const prevCaption = previousRangeCaption(prevRange.from, prevRange.to, preset);

  const load = useCallback(async () => {
    if (inclusiveDaySpan(from, to) > MAX_RANGE_DAYS) {
      setDashboard(null);
      setPrevious(null);
      setVisits([]);
      setPrevVisits([]);
      setError(`Date range cannot exceed ${MAX_RANGE_DAYS} days.`);
      setLoading(false);
      return;
    }
    setLoading(true);
    fullLoadInFlight.current = true;
    setError('');
    const params = { from, to, type: typeFilter, status: statusFilter };
    const prevParams = { from: prevRange.from, to: prevRange.to, type: typeFilter, status: statusFilter };
    const liveParams = { from: addDaysIso(today, -2), to: today, type: 'all', status: 'all' };
    try {
      const [dash, rows, prevDash, prevRows, liveRows] = await Promise.all([
        apiService.getTeleconsultManagementDashboard(params),
        apiService.getTeleconsultManagementVisits(params),
        apiService.getTeleconsultManagementDashboard(prevParams).catch(() => null),
        apiService.getTeleconsultManagementVisits(prevParams).catch(() => [] as TeleconsultManagementVisit[]),
        apiService.getTeleconsultManagementVisits(liveParams).catch(() => [] as TeleconsultManagementVisit[]),
      ]);
      setDashboard(dash);
      setVisits(rows);
      setPrevious(prevDash);
      setPrevVisits(prevRows || []);
      setLiveVisits(liveRows || []);
    } catch (err: unknown) {
      setDashboard(null);
      setPrevious(null);
      setVisits([]);
      setPrevVisits([]);
      setLiveVisits([]);
      setError(getApiErrorMessage(err, 'Could not load management data.'));
    } finally {
      fullLoadInFlight.current = false;
      setLoading(false);
    }
  }, [from, to, typeFilter, statusFilter, prevRange.from, prevRange.to, today]);

  const refreshPatientLog = useCallback(async () => {
    if (inclusiveDaySpan(from, to) > MAX_RANGE_DAYS) return;
    const rows = await apiService.getTeleconsultManagementVisits({
      from,
      to,
      type: typeFilter,
      status: statusFilter,
    });
    setVisits(rows || []);
  }, [from, to, typeFilter, statusFilter]);

  const refreshLiveQueue = useCallback(async () => {
    const day = getTodayISO();
    try {
      const liveRows = await apiService.getTeleconsultManagementVisits({
        from: addDaysIso(day, -2),
        to: day,
        type: 'all',
        status: 'all',
      });
      setLiveVisits(liveRows || []);
    } catch {
      // Keep the last snapshot. The next poll retries.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let intervalId: number | undefined;
    let inFlight = false;

    const poll = async () => {
      if (inFlight) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        await refreshLiveQueue();
      } finally {
        inFlight = false;
      }
    };

    intervalId = window.setInterval(() => void poll(), MANAGEMENT_POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void poll();
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalId != null) window.clearInterval(intervalId);
    };
  }, [refreshLiveQueue]);

  useEffect(() => {
    if (subTab !== 'log') return undefined;

    let intervalId: number | undefined;
    let inFlight = false;

    const poll = async () => {
      if (inFlight || fullLoadInFlight.current) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        await refreshPatientLog();
      } catch {
        // Keep the last log. The next poll retries.
      } finally {
        inFlight = false;
      }
    };

    void poll();
    intervalId = window.setInterval(() => void poll(), MANAGEMENT_POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void poll();
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalId != null) window.clearInterval(intervalId);
    };
  }, [subTab, refreshPatientLog]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(id);
  }, []);

  const typeOptions = useMemo(() => {
    const extras = (dashboard?.byType ?? []).map((x) => x.type);
    return Array.from(new Set(['Stripe', 'CSGF', 'SSD', ...extras].filter(Boolean)));
  }, [dashboard]);

  const doctorOptions = useMemo(() => {
    const names = visits.map((v) => (v.doctorName || '').trim()).filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [visits]);

  const scopedVisits = useMemo(() => {
    if (doctorFilter === 'all') return visits;
    return visits.filter((v) => doctorKey(v) === doctorFilter);
  }, [visits, doctorFilter]);

  const scopedPrevVisits = useMemo(() => {
    if (doctorFilter === 'all') return prevVisits;
    return prevVisits.filter((v) => doctorKey(v) === doctorFilter);
  }, [prevVisits, doctorFilter]);

  const stats = useMemo(() => {
    if (doctorFilter === 'all' && dashboard) return dashboard;
    return summarizeVisits(scopedVisits);
  }, [doctorFilter, dashboard, scopedVisits]);

  const prevStats = useMemo(() => {
    if (doctorFilter === 'all' && previous) return previous;
    return scopedPrevVisits.length || doctorFilter !== 'all' ? summarizeVisits(scopedPrevVisits) : previous;
  }, [doctorFilter, previous, scopedPrevVisits]);

  const logFilters: LogFilterState = useMemo(
    () => ({
      search,
      staffNames: staffFilter,
      documentLabels: docsFilter,
      reasonPreset,
    }),
    [search, staffFilter, docsFilter, reasonPreset],
  );

  const staffOptions = useMemo(() => distinctStaffNames(scopedVisits), [scopedVisits]);
  const docOptions = useMemo(() => distinctDocumentLabels(scopedVisits), [scopedVisits]);

  const logVisits = useMemo(
    () => filterAndSortLogVisits(scopedVisits, logFilters),
    [scopedVisits, logFilters],
  );

  const logFiltersActive = hasActiveLogFilters(logFilters);

  const clearLogFilters = () => {
    setSearch('');
    setStaffFilter([]);
    setDocsFilter([]);
    setReasonPreset('any');
    setDoctorFilter('all');
    setTypeFilter('all');
    setStatusFilter('all');
  };

  const chartSeries = useMemo(
    () => buildVisitChartSeries(scopedVisits, from, to, preset),
    [scopedVisits, from, to, preset],
  );
  const daily = chartSeries.buckets;
  const chartSubtitle = visitChartSubtitle(preset, from, to, chartSeries.byMonth);

  const spark = useMemo(() => {
    const byBucket = (status?: string) =>
      daily.map((d) => {
        if (status === 'completed') return d.completed;
        if (status === 'cancelled') return d.cancelled;
        if (status === 'refunded') return d.refunded;
        return d.completed + d.cancelled + d.refunded;
      });
    return {
      total: byBucket(),
      completed: byBucket('completed'),
      cancelled: byBucket('cancelled'),
      refunded: byBucket('refunded'),
    };
  }, [daily]);

  const live = useMemo(() => {
    const open = liveVisits.filter((v) => v.status === 'open' || v.status === 'completed');
    const waitingId = liveVisits.filter(
      (v) => v.status === 'open' && v.newPt && !v.idVerifiedAt,
    );
    const waitingDoctor = liveVisits.filter(
      (v) => v.status === 'open' && v.patientReadyAt && !v.doctorPickedAt,
    );
    const inConsult = liveVisits.filter(
      (v) => v.status === 'open' && v.doctorPickedAt && !v.consultCompletedAt,
    );
    const pendingJulius = liveVisits.filter(
      (v) => v.status !== 'cancelled' && Boolean(v.consultCompletedAt) && !v.juliusCheckoutAt,
    );
    const oldestWait = waitingDoctor
      .map((v) => waitSeconds(v.patientReadyAt, now))
      .filter((s): s is number => s != null)
      .sort((a, b) => b - a)[0];
    const consultDoc = inConsult[0];
    const consultWait = consultDoc ? waitSeconds(consultDoc.doctorPickedAt, now) : null;
    return {
      waitingId: waitingId.length,
      waitingDoctor: waitingDoctor.length,
      oldestWait,
      inConsult: inConsult.length,
      consultName: consultDoc?.doctorName || '',
      consultWait,
      pendingJulius: pendingJulius.length,
      open: open.length,
    };
  }, [liveVisits, now]);

  const kpis = useMemo(() => {
    const completed = scopedVisits.filter((v) => v.status === 'completed');
    const stripe = completed.filter((v) => v.type === 'Stripe');
    const med = (
      rows: TeleconsultManagementVisit[],
      fromKey: keyof TeleconsultManagementVisit,
      toKey: keyof TeleconsultManagementVisit,
    ) =>
      median(
        rows
          .map((r) => secondsBetween(r[fromKey] as string | null, r[toKey] as string | null))
          .filter((s): s is number => s != null),
      );
    return {
      pay: median(
        stripe
          .map((r) => secondsBetweenRegisterAndStripePay(r.registeredAt, r.stripePaidAt))
          .filter((s): s is number => s != null),
      ),
      /** Upload → DateVetted (attachment wait). */
      id: median(
        completed
          .map((r) => idVerificationSampleSeconds(r))
          .filter((sec): sec is number => sec != null),
      ),
      /** Register → ID verified (cumulative gate; used by end-to-end timestamp chart). */
      idFromRegister: med(completed, 'registeredAt', 'idVerifiedAt'),
      start: med(completed, 'registeredAt', 'doctorPickedAt'),
      consult: med(completed, 'doctorPickedAt', 'consultCompletedAt'),
      checkout: med(completed, 'consultCompletedAt', 'juliusCheckoutAt'),
      e2e: med(completed, 'registeredAt', 'juliusCheckoutAt'),
      ready: med(completed, 'registeredAt', 'patientReadyAt'),
    };
  }, [scopedVisits]);

  const byDoctorRows = useMemo(() => {
    const completed = scopedVisits.filter((v) => v.status === 'completed' && v.doctorName);
    const groups = new Map<string, TeleconsultManagementVisit[]>();
    completed.forEach((v) => {
      const k = doctorKey(v);
      const list = groups.get(k) || [];
      list.push(v);
      groups.set(k, list);
    });
    return Array.from(groups.entries())
      .map(([name, rows]) => ({
        name,
        count: rows.length,
        medianConsult: median(
          rows
            .map((r: TeleconsultManagementVisit) => secondsBetween(r.doctorPickedAt, r.consultCompletedAt))
            .filter((s): s is number => s != null),
        ),
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [scopedVisits]);

  const byStaffRows = useMemo(() => {
    const groups = new Map<string, TeleconsultManagementVisit[]>();
    scopedVisits.forEach((v) => {
      const k = staffKey(v);
      const list = groups.get(k) || [];
      list.push(v);
      groups.set(k, list);
    });
    return Array.from(groups.entries())
      .map(([name, rows]) => ({
        name,
        count: rows.length,
        detail: formatStaffStepMix(rows),
        medianConsult: median(
          rows
            .filter((r: TeleconsultManagementVisit) => r.status === 'completed')
            .map((r: TeleconsultManagementVisit) => staffHandlingSampleSeconds(r))
            .filter((s): s is number => s != null),
        ),
      }))
      .sort((a, b) => {
        if (a.name === 'System' && b.name !== 'System') return 1;
        if (b.name === 'System' && a.name !== 'System') return -1;
        return b.count - a.count || a.name.localeCompare(b.name);
      });
  }, [scopedVisits]);

  const paidStripe = scopedVisits.filter((v) => v.type === 'Stripe' && (v.stripeAmountSgd || 0) > 0).length;
  const nonStripe = scopedVisits.filter((v) => isNonStripe(v.type)).length;

  const clock = now.toLocaleTimeString('en-GB', {
    timeZone: TELECONSULT_QUEUE_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const openAction = (kind: ActionKind, visit: TeleconsultManagementVisit) => {
    setActionKind(kind);
    setActionVisit(visit);
    setReasonText('');
    setCancelReasonCode('');
    setReasonError('');
    setRefundMode('full');
    setCustomRefundSgd('');
  };

  const closeAction = () => {
    if (reasonBusy) return;
    setActionKind(null);
    setActionVisit(null);
    setReasonText('');
    setCancelReasonCode('');
    setReasonError('');
    setRefundMode('full');
    setCustomRefundSgd('');
  };

  useEffect(() => {
    if (!actionKind) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAction();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actionKind, reasonBusy]);

  const refundRemaining = actionVisit ? visitRemainingSgd(actionVisit) : 0;
  const canRetain2 = refundRemaining > REFUND_RETAIN_SGD;
  const refundPreview =
    actionKind === 'refund' ? refundPreviewSgd(refundRemaining, refundMode, customRefundSgd) : null;

  const submitAction = async () => {
    if (!actionKind || !actionVisit) return;
    const trimmed = reasonText.trim();
    if (actionKind === 'cancel') {
      if (!isVisitCancelReasonValid(cancelReasonCode, reasonText)) {
        setReasonError(
          cancelReasonCode === VISIT_CANCEL_REASON_OTHER || cancelReasonCode === ''
            ? 'Select a reason. For Other, enter at least 3 characters.'
            : 'Select a cancellation reason.',
        );
        return;
      }
    } else if (trimmed.length < 3) {
      setReasonError('Please enter a reason (at least 3 characters).');
      return;
    }
    if (actionKind === 'refund') {
      if (refundRemaining < 0.01) {
        setReasonError('There is no remaining Stripe amount to refund.');
        return;
      }
      if (refundMode === 'retain2' && !canRetain2) {
        setReasonError(
          `Cannot keep SGD ${REFUND_RETAIN_SGD.toFixed(2)} — remaining is only ${formatSgd(refundRemaining)}.`,
        );
        return;
      }
      if (refundPreview == null) {
        setReasonError(
          refundMode === 'custom'
            ? `Enter a custom amount between SGD 0.01 and ${formatSgd(refundRemaining).replace('SGD ', '')}.`
            : 'Choose how much to refund.',
        );
        return;
      }
    }
    setReasonBusy(true);
    setReasonError('');
    try {
      if (actionKind === 'cancel') {
        await apiService.cancelTeleconsultManagementVisit(actionVisit.visitUlid, trimmed);
      } else {
        await apiService.refundTeleconsultManagementVisit(
          actionVisit.visitUlid,
          trimmed,
          refundMode,
          refundMode === 'custom' ? refundPreview ?? undefined : undefined,
        );
      }
      setActionKind(null);
      setActionVisit(null);
      setReasonText('');
      setRefundMode('full');
      setCustomRefundSgd('');
      await load();
    } catch (err: unknown) {
      setReasonError(
        getApiErrorMessage(
          err,
          actionKind === 'cancel' ? 'Could not cancel this visit.' : 'Could not refund this payment in Stripe.',
        ),
      );
    } finally {
      setReasonBusy(false);
    }
  };

  const onPreset = (id: DatePreset) => {
    const next = applyPresetRange(id, today);
    setFrom(next.from);
    setTo(next.to);
  };

  const rangeDays = inclusiveDaySpan(from, to);
  const selectedPreset = preset;

  return (
    <section className="mdash">
      <div className="mdash-sticky">
      <header className="mdash-head">
        <div>
          <h1 className="mdash-title">Management Dashboard</h1>
          <p className="mdash-sub">
            Day-to-day stats for offsite teleconsult visits — sales, patients, turnaround times and the patient log.
          </p>
        </div>
        <p className="mdash-live">
          <span className="mdash-live-dot" />
          Live · updated {clock}
          {live.inConsult > 0
            ? ` · ${live.inConsult} consult${live.inConsult === 1 ? '' : 's'} in progress`
            : ''}
        </p>
      </header>

      <div className="mdash-filters">
        <div className="mdash-presets" role="group" aria-label="Date presets">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`mdash-preset${selectedPreset === p.id ? ' mdash-preset--on' : ''}`}
              onClick={() => onPreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <ManagementDateRangePicker
          from={from}
          to={to}
          onChange={(nextFrom, nextTo) => {
            setFrom(nextFrom);
            setTo(nextTo);
          }}
        />
        <label className="mdash-select">
          <span className="visually-hidden">Visit type</span>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Visit type">
            <option value="all">All types</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="mdash-select">
          <span className="visually-hidden">Visit status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Visit status">
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="mdash-select">
          <span className="visually-hidden">Doctor</span>
          <select value={doctorFilter} onChange={(e) => setDoctorFilter(e.target.value)} aria-label="Doctor">
            <option value="all">All doctors</option>
            {doctorOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <MultiCheckDropdown
          label="Staff"
          emptyLabel="All staff"
          options={staffOptions}
          value={staffFilter}
          onChange={setStaffFilter}
        />
        <MultiCheckDropdown
          label="Docs"
          emptyLabel="All docs"
          options={docOptions}
          value={docsFilter}
          onChange={setDocsFilter}
        />
        <label className="mdash-select">
          <span className="visually-hidden">Reason</span>
          <select
            value={reasonPreset}
            onChange={(e) => setReasonPreset(e.target.value as ReasonPreset)}
            aria-label="Reason"
          >
            {REASON_PRESET_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="mdash-search">
          <span className="visually-hidden">Search visits</span>
          <input
            type="search"
            placeholder="Search name, NRIC, staff, doctor, reason, docs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        {(logFiltersActive || doctorFilter !== "all" || typeFilter !== "all" || statusFilter !== "all") ? (
          <button type="button" className="mdash-clear-filters" onClick={clearLogFilters}>
            Clear filters
          </button>
        ) : null}
      </div>

      <div className="mdash-tabs" role="tablist" aria-label="Management views">
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'dashboard'}
          className={`mdash-tab${subTab === 'dashboard' ? ' mdash-tab--on' : ''}`}
          onClick={() => setSubTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'log'}
          className={`mdash-tab${subTab === 'log' ? ' mdash-tab--on' : ''}`}
          onClick={() => setSubTab('log')}
        >
          Patient log <em>{logVisits.length}</em>
        </button>
      </div>
      </div>

      <div className="mdash-scroll">
      {error ? <div className="staff-tele-alert staff-tele-alert--error">{error}</div> : null}
      {loading ? <div className="staff-tele-loading">Loading management data…</div> : null}

      {!loading && !error && subTab === 'dashboard' && stats ? (
        <div className="mdash-body">
          <div className="mdash-kpis">
            <KpiCard
              icon="▣"
              label="Total visits"
              value={stats.total}
              meta={`${stats.uniquePatients} unique · ${stats.newPatients} new${
                stats.open > 0 ? ` · ${stats.open} open` : ''
              }`}
              delta={deltaLine(stats.total, prevStats?.total, prevCaption, false)}
              spark={spark.total}
              sparkColor="#166534"
            />
            <KpiCard
              icon="✓"
              label="Completed"
              value={stats.completed}
              meta={`${pct(stats.completed, stats.total)} of visits`}
              delta={deltaLine(stats.completed, prevStats?.completed, prevCaption, false)}
              spark={spark.completed}
              sparkColor="#166534"
              actionLabel="breakdown"
              onAction={() => setReasonPopup('completed')}
            />
            <KpiCard
              icon="×"
              label="Cancelled"
              value={stats.cancelled}
              meta={`${pct(stats.cancelled, stats.total)} of visits`}
              delta={deltaLine(stats.cancelled, prevStats?.cancelled, prevCaption, true)}
              spark={spark.cancelled}
              sparkColor="#c2410c"
              tone="cancel"
              actionLabel="reasons"
              onAction={() => setReasonPopup('cancel')}
            />
            <KpiCard
              icon="↩"
              label="Refunded"
              value={stats.refunded}
              meta={`${pct(stats.refunded, stats.total)} of visits${
                stats.stripeRefundedSgd > 0 ? ` · ${formatSgd(stats.stripeRefundedSgd)}` : ''
              }`}
              delta={deltaLine(stats.refunded, prevStats?.refunded, prevCaption, true)}
              spark={spark.refunded}
              sparkColor="#d97706"
              tone="refund"
              actionLabel="reasons"
              onAction={() => setReasonPopup('refund')}
            />
          </div>

          <div className="mdash-mid">
            <article className="mdash-card mdash-card--chart">
              <header className="mdash-card-head">
                <div>
                  <h2>{chartSeries.byMonth ? 'Visits per month' : 'Visits per day'}</h2>
                  <p>
                    {chartSubtitle}
                  </p>
                </div>
                <ul className="mdash-legend">
                  <li>
                    <i className="mdash-swatch mdash-swatch--ok" /> Completed
                  </li>
                  <li>
                    <i className="mdash-swatch mdash-swatch--cancel" /> Cancelled
                  </li>
                  <li>
                    <i className="mdash-swatch mdash-swatch--refund" /> Refunded
                  </li>
                </ul>
              </header>
              <StackedBars days={daily} byMonth={chartSeries.byMonth} />
            </article>

            <article className="mdash-card">
              <header className="mdash-card-head">
                <div>
                  <h2>Sales</h2>
                  <p>Stripe-collected, this range</p>
                </div>
              </header>
              <p className="mdash-sales-net">{formatSgd(stats.stripeNetSgd)}</p>
              <p className="mdash-sales-note">
                net of {formatSgd(stats.stripeRefundedSgd)} refunded
              </p>
              <dl className="mdash-sales-rows">
                <div>
                  <dt>Gross collected</dt>
                  <dd>{formatSgd(stats.stripeCollectedSgd)}</dd>
                </div>
                <div>
                  <dt>Refunded ({stats.refunded})</dt>
                  <dd className="mdash-neg">
                    {stats.stripeRefundedSgd > 0 ? `−${formatSgd(stats.stripeRefundedSgd).replace('SGD ', '')}` : '—'}
                  </dd>
                </div>
                <div>
                  <dt>Paid (Stripe) visits</dt>
                  <dd>{paidStripe}</dd>
                </div>
                <div>
                  <dt>Non-Stripe (CSGF / SSD)</dt>
                  <dd>{nonStripe}</dd>
                </div>
              </dl>
            </article>

            <article className="mdash-card">
              <header className="mdash-card-head">
                <div>
                  <h2>Right now</h2>
                  <p>Live queue — not affected by the date filter</p>
                </div>
              </header>
              <ul className="mdash-now">
                <li>
                  <strong className="mdash-now-n">{live.waitingId}</strong>
                  <span>
                    <b>Waiting ID verification</b>
                    <em>Registered, ID not yet checked</em>
                  </span>
                </li>
                <li className={live.waitingDoctor > 0 ? 'mdash-now--warn' : ''}>
                  <strong className="mdash-now-n">{live.waitingDoctor}</strong>
                  <span>
                    <b>Waiting for doctor</b>
                    <em>Ready, not yet picked</em>
                  </span>
                  {live.oldestWait != null ? (
                    <aside>oldest {Math.max(1, Math.round(live.oldestWait / 60))} min</aside>
                  ) : null}
                </li>
                <li className={live.inConsult > 0 ? 'mdash-now--go' : ''}>
                  <strong className="mdash-now-n">{live.inConsult}</strong>
                  <span>
                    <b>In consultation</b>
                    <em>
                      {live.consultName
                        ? `${live.consultName}${live.consultWait != null ? ` · ${Math.max(1, Math.round(live.consultWait / 60))} min` : ''}`
                        : 'None'}
                    </em>
                  </span>
                </li>
                <li>
                  <strong className="mdash-now-n">{live.pendingJulius}</strong>
                  <span>
                    <b>Pending Julius checkout</b>
                    <em>Consult ended, not checked out</em>
                  </span>
                </li>
              </ul>
            </article>
          </div>

          <div className="mdash-split">
            <article className="mdash-card">
              <header className="mdash-card-head">
                <div>
                  <h2>By type</h2>
                  <p>All visits in range · share</p>
                </div>
              </header>
              <TypeDonut rows={stats.byType} total={stats.total} />
            </article>
            <article className="mdash-card">
              <header className="mdash-card-head">
                <div>
                  <h2>By doctor</h2>
                  <p>Completed consults · median consult time</p>
                </div>
              </header>
              <HBars rows={byDoctorRows} />
            </article>
            <article className="mdash-card">
              <header className="mdash-card-head">
                <div>
                  <h2>By staff</h2>
                  <p>Clinic staff who handled the visit · first ops step</p>
                </div>
              </header>
              <HBars rows={byStaffRows} tone="teal" />
            </article>
          </div>

          <article className="mdash-card mdash-journey">
            <header className="mdash-card-head">
              <div>
                <h2>Time averages — patient journey</h2>
                <p>Median across completed visits in this range · tick = target time · green = within target, red = over</p>
              </div>
              <ul className="mdash-legend">
                <li>
                  <i className="mdash-swatch mdash-swatch--ok" /> Within target
                </li>
                <li>
                  <i className="mdash-swatch mdash-swatch--cancel" /> Over target
                </li>
              </ul>
            </header>
            <div className="mdash-journey-grid">
              <div className="mdash-kpi-list">
                <KpiBar label="Register → Payment" hint="Stripe only" value={kpis.pay} target={KPI.pay} />
                <KpiBar label="Pending ID → Verified" hint="Attachment upload → DateVetted (not Register→ID)" value={kpis.id} target={KPI.id} />
                <KpiBar label="Register → Start of consultation" value={kpis.start} target={KPI.start} />
                <KpiBar label="Start → End of consultation" value={kpis.consult} target={KPI.consult} />
                <KpiBar label="Consultation end → Checkout" value={kpis.checkout} target={KPI.checkout} />
                <KpiBar label="Register → Checkout" value={kpis.e2e} target={KPI.e2e} />
              </div>
              <EndToEndFlow kpis={kpis} />
            </div>
          </article>
        </div>
      ) : null}

      {!loading && !error && subTab === 'log' ? (
        <div className="mdash-log">
          {logVisits.length === 0
            ? logFiltersActive
              ? (
                  <p className="mdash-empty">
                    No visits match filters.{" "}
                    <button type="button" className="mdash-linkish" onClick={clearLogFilters}>
                      Clear filters
                    </button>
                  </p>
                )
              : (
                  <p className="mdash-empty">No visits in this range.</p>
                )
            : (
            <table className="mdash-log-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Staff</th>
                  <th>Type · amount</th>
                  <th>Status</th>
                  <th>Journey</th>
                  <th>Reason</th>
                  <th>Medical / Memo</th>
                </tr>
              </thead>
              <tbody>
                {logVisits.map((v) => (
                  <LogRow key={v.visitUlid} visit={v} onAction={openAction} onOpenChat={setChatVisit} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
      </div>

      {chatVisit ? (
        <ChatTranscriptDialog visit={chatVisit} onClose={() => setChatVisit(null)} />
      ) : null}

      {reasonPopup ? (
        <ReasonSummaryDialog
          kind={reasonPopup}
          stats={stats}
          typeRows={
            reasonPopup === 'completed'
              ? summarizeVisits(scopedVisits.filter((v) => v.status === 'completed')).byType
              : undefined
          }
          from={from}
          to={to}
          onClose={() => setReasonPopup(null)}
        />
      ) : null}

      {actionKind && actionVisit ? createPortal(
        <div className="staff-mgmt__dialog-backdrop" role="presentation" onClick={closeAction}>
          <div
            className="staff-mgmt__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="staff-mgmt-reason-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="staff-mgmt__dialog-title" id="staff-mgmt-reason-title">
              {actionKind === 'cancel' ? 'Cancel visit' : 'Refund in Stripe'}
            </h2>
            <p className="staff-mgmt__dialog-lead">
              {actionVisit.patientName || 'Patient'} · {actionVisit.maskedNric || '—'} · {actionVisit.type}
              {actionKind === 'refund'
                ? ' — this sends the money back through Stripe.'
                : ' — requires a reason shown on the management log.'}
            </p>
            {actionKind === 'refund' ? (
              <div className="staff-mgmt__refund-box">
                <p className="staff-mgmt__refund-summary">
                  Paid {formatSgd(actionVisit.stripeAmountSgd)}
                  {visitRefundedSgd(actionVisit) > 0
                    ? ` · already refunded ${formatSgd(visitRefundedSgd(actionVisit))}`
                    : ''}
                  {' · remaining '}
                  <strong>{formatSgd(refundRemaining)}</strong>
                </p>
                <fieldset className="staff-mgmt__refund-modes" disabled={reasonBusy}>
                  <legend className="staff-mgmt__dialog-label">Refund amount</legend>
                  <label className="staff-mgmt__refund-choice">
                    <input
                      type="radio"
                      name="staff-mgmt-refund-mode"
                      checked={refundMode === 'full'}
                      onChange={() => setRefundMode('full')}
                    />
                    Full refund ({formatSgd(refundRemaining)})
                  </label>
                  {canRetain2 ? (
                    <label className="staff-mgmt__refund-choice">
                      <input
                        type="radio"
                        name="staff-mgmt-refund-mode"
                        checked={refundMode === 'retain2'}
                        onChange={() => setRefundMode('retain2')}
                      />
                      Charge $2 (refund {formatSgd(refundRemaining - REFUND_RETAIN_SGD)}, keep SGD 2.00)
                    </label>
                  ) : null}
                  <label className="staff-mgmt__refund-choice">
                    <input
                      type="radio"
                      name="staff-mgmt-refund-mode"
                      checked={refundMode === 'custom'}
                      onChange={() => setRefundMode('custom')}
                    />
                    Custom amount
                  </label>
                  {refundMode === 'custom' ? (
                    <input
                      type="number"
                      className="staff-mgmt__dialog-input staff-mgmt__refund-custom"
                      min={0.01}
                      max={refundRemaining}
                      step={0.01}
                      inputMode="decimal"
                      placeholder="e.g. 10.00"
                      value={customRefundSgd}
                      disabled={reasonBusy}
                      onChange={(e) => setCustomRefundSgd(e.target.value)}
                    />
                  ) : null}
                </fieldset>
                <p className="staff-mgmt__refund-preview">
                  {refundPreview != null
                    ? `Patient receives ${formatSgd(refundPreview)}${
                        refundRemaining - refundPreview >= 0.01
                          ? ` · clinic keeps ${formatSgd(refundRemaining - refundPreview)}`
                          : ''
                      }`
                    : refundMode === 'custom'
                      ? 'Enter a custom amount to preview.'
                      : '—'}
                </p>
              </div>
            ) : null}
            {actionKind === 'cancel' ? (
              <>
                <p id="staff-mgmt-cancel-reason-label" className="staff-mgmt__dialog-label">
                  Cancellation reason
                </p>
                <VisitCancelReasonPicker
                  name="staff-mgmt-cancel-reason"
                  labelledBy="staff-mgmt-cancel-reason-label"
                  value={cancelReasonCode}
                  disabled={reasonBusy}
                  onChange={(code) => {
                    setCancelReasonCode(code);
                    if (code !== VISIT_CANCEL_REASON_OTHER) setReasonText('');
                  }}
                />
                {cancelReasonCode === VISIT_CANCEL_REASON_OTHER ? (
                  <>
                    <label className="staff-mgmt__dialog-label" htmlFor="staff-mgmt-reason">
                      Please specify
                    </label>
                    <textarea
                      id="staff-mgmt-reason"
                      className="staff-mgmt__dialog-input"
                      rows={3}
                      value={reasonText}
                      disabled={reasonBusy}
                      placeholder="Describe the reason (min 3 characters)"
                      onChange={(e) => setReasonText(e.target.value)}
                    />
                  </>
                ) : null}
              </>
            ) : (
              <>
                <label className="staff-mgmt__dialog-label" htmlFor="staff-mgmt-reason">
                  Reason
                </label>
                <textarea
                  id="staff-mgmt-reason"
                  className="staff-mgmt__dialog-input"
                  rows={3}
                  value={reasonText}
                  disabled={reasonBusy}
                  placeholder="e.g. Duplicate payment"
                  onChange={(e) => setReasonText(e.target.value)}
                />
              </>
            )}
            {reasonError ? <p className="staff-mgmt__dialog-error">{reasonError}</p> : null}
            <div className="staff-mgmt__dialog-buttons">
              <button type="button" className="staff-mgmt__action" disabled={reasonBusy} onClick={closeAction}>
                Back
              </button>
              <button
                type="button"
                className="staff-mgmt__action staff-mgmt__action--warn"
                disabled={
                  reasonBusy
                  || (actionKind === 'refund' && refundPreview == null)
                  || (actionKind === 'cancel' && !isVisitCancelReasonValid(cancelReasonCode, reasonText))
                }
                onClick={() => void submitAction()}
              >
                {reasonBusy ? 'Saving…' : actionKind === 'cancel' ? 'Confirm cancel' : `Refund ${formatSgd(refundPreview)}`}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </section>
  );
}

function deltaLine(
  current: number,
  previous: number | undefined,
  caption: string,
  invert: boolean,
): { text: string; tone: 'up' | 'down' | 'flat' } | null {
  if (previous == null) return null;
  const diff = current - previous;
  if (diff === 0) return { text: `— same vs ${caption} (${previous})`, tone: 'flat' };
  const up = diff > 0;
  const pctVal = previous === 0 ? null : Math.round((Math.abs(diff) / previous) * 100);
  const good = invert ? !up : up;
  return {
    text: `${up ? '▲' : '▼'} ${pctVal == null ? Math.abs(diff) : `${pctVal}%`} vs ${caption} (${previous})`,
    tone: good ? 'up' : 'down',
  };
}

function KpiCard({
  icon,
  label,
  value,
  meta,
  delta,
  spark,
  sparkColor,
  tone,
  actionLabel,
  onAction,
}: {
  icon: string;
  label: string;
  value: number;
  meta: string;
  delta: { text: string; tone: 'up' | 'down' | 'flat' } | null;
  spark: number[];
  sparkColor: string;
  tone?: 'cancel' | 'refund';
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <article className={`mdash-card mdash-kpi${tone ? ` mdash-kpi--${tone}` : ''}`}>
      <header className="mdash-kpi-top">
        <span className="mdash-kpi-icon" aria-hidden>
          {icon}
        </span>
        <h3>{label}</h3>
        {actionLabel && onAction ? (
          <button type="button" className="mdash-link" onClick={onAction}>
            {actionLabel}
          </button>
        ) : null}
      </header>
      <div className="mdash-kpi-main">
        <div>
          <p className="mdash-kpi-value">{value}</p>
          <p className="mdash-kpi-meta">{meta}</p>
          {delta ? <p className={`mdash-delta mdash-delta--${delta.tone}`}>{delta.text}</p> : null}
        </div>
        <Sparkline values={spark} color={sparkColor} />
      </div>
    </article>
  );
}

function Sparkline({ values, color }: { values: number[]; color: string }) {
  const w = 120;
  const h = 36;
  const p = 2;
  if (values.length < 2) return <svg className="mdash-spark" width={w} height={h} aria-hidden />;
  const max = Math.max(...values, 1);
  const pts = values
    .map((v, i) => {
      const x = p + (i / (values.length - 1)) * (w - 2 * p);
      const y = h - p - (v / max) * (h - 2 * p);
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg className="mdash-spark" viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden>
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={pts} />
    </svg>
  );
}

function niceYAxis(rawMax: number): { max: number; ticks: number[] } {
  const n = Math.max(1, rawMax);
  const mag = 10 ** Math.floor(Math.log10(n));
  const nrm = n / mag;
  const niceNrm = nrm <= 1 ? 1 : nrm <= 2 ? 2 : nrm <= 4 ? 4 : nrm <= 5 ? 5 : nrm <= 8 ? 8 : 10;
  const max = niceNrm * mag;
  const step = max / 4;
  const ticks: number[] = [];
  for (let v = max; v >= 0 - 1e-9; v -= step) {
    ticks.push(Math.round(v));
  }
  return { max, ticks };
}

function StackedBars({
  days,
  byMonth,
}: {
  days: VisitChartBucket[];
  byMonth: boolean;
}) {
  const rawMax = Math.max(1, ...days.map((d) => d.completed + d.cancelled + d.refunded));
  const { max, ticks } = niceYAxis(rawMax);
  const denseDaily = isDenseDailyChart(days.length, byMonth);
  return (
    <div className={`mdash-chart${denseDaily ? ' mdash-chart--dense' : ''}`}>
      <div className="mdash-bars-y" aria-hidden="true">
        {ticks.map((t, i) => (
          <span key={`${t}-${i}`}>{t}</span>
        ))}
      </div>
      <div
        className={`mdash-bars${denseDaily ? ' mdash-bars--dense' : ''}`}
        role="img"
        aria-label="Visits chart"
      >
        {days.map((d, i) => {
          const total = d.completed + d.cancelled + d.refunded;
          // Fill height = share of Y-max. Segments use flex-grow shares of that fill
          // (avoids %-rounding gaps). Fixed plot/label grid keeps every bar on the Y=0 baseline.
          const fillPct = total > 0 ? (total / max) * 100 : 0;
          const showLabel = shouldShowVisitChartAxisLabel(days, i, denseDaily);
          const label = visitChartAxisLabel(d, denseDaily);
          return (
            <div
              key={d.key}
              className="mdash-bar"
              title={`${d.label}: ${d.completed} completed, ${d.cancelled} cancelled, ${d.refunded} refunded`}
            >
              <div className="mdash-bar-plot">
                {total > 0 ? (
                  <div className="mdash-bar-stack" style={{ height: `${fillPct}%` }}>
                    {/* column-reverse: first in DOM sits on the baseline. Completed = foundation. */}
                    {d.completed > 0 ? (
                      <span
                        className="mdash-bar-seg mdash-bar-seg--ok"
                        style={{ flexGrow: d.completed, background: COMPLETED_BAR_COLOR }}
                      />
                    ) : null}
                    {d.cancelled > 0 ? (
                      <span
                        className="mdash-bar-seg mdash-bar-seg--cancel"
                        style={{ flexGrow: d.cancelled }}
                      />
                    ) : null}
                    {d.refunded > 0 ? (
                      <span
                        className="mdash-bar-seg mdash-bar-seg--refund"
                        style={{ flexGrow: d.refunded }}
                      />
                    ) : null}
                  </div>
                ) : (
                  <div className="mdash-bar-stack mdash-bar-stack--empty" aria-hidden="true" />
                )}
              </div>
              {showLabel ? (
                <span className="mdash-bar-label">{label}</span>
              ) : (
                <span className="mdash-bar-label" aria-hidden="true" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TypeDonut({ rows, total }: { rows: TeleconsultManagementTypeCount[]; total: number }) {
  const r = 42;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const slices = rows.map((row) => {
    const frac = total > 0 ? row.count / total : 0;
    const slice = { ...row, frac, dash: frac * c, offset };
    offset += frac * c;
    return slice;
  });
  return (
    <div className="mdash-donut">
      <svg viewBox="0 0 120 120" width="140" height="140" aria-hidden>
        <circle cx="60" cy="60" r={r} fill="none" stroke="#e7eee8" strokeWidth="14" />
        {slices.map((s) => (
          <circle
            key={s.type}
            cx="60"
            cy="60"
            r={r}
            fill="none"
            stroke={typeColor(s.type)}
            strokeWidth="14"
            strokeDasharray={`${s.dash} ${c - s.dash}`}
            strokeDashoffset={-s.offset}
            strokeLinecap="butt"
            transform="rotate(-90 60 60)"
          />
        ))}
        <text x="60" y="56" textAnchor="middle" className="mdash-donut-n">
          {total}
        </text>
        <text x="60" y="72" textAnchor="middle" className="mdash-donut-l">
          visits
        </text>
      </svg>
      <ul>
        {rows.length === 0 ? (
          <li className="mdash-empty">No visits in this range.</li>
        ) : (
          rows.map((row) => (
            <li key={row.type}>
              <i className="mdash-swatch" style={{ background: typeColor(row.type) }} />
              <span>{row.type}</span>
              <strong>{row.count}</strong>
              <em>{pct(row.count, total).replace('.0%', '%')}</em>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function HBars({
  rows,
  tone = 'blue',
}: {
  rows: { name: string; count: number; medianConsult: number | null; detail?: string }[];
  tone?: 'blue' | 'teal';
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0) return <p className="mdash-empty">No rows in this range.</p>;
  return (
    <ul className={`mdash-hbars mdash-hbars--${tone}`}>
      {rows.map((row) => (
        <li key={row.name}>
          <span className="mdash-hbar-name">
            {row.name}
            {row.detail ? <span className="mdash-hbar-detail">{row.detail}</span> : null}
            {row.medianConsult != null ? <em>{formatDurationCompact(row.medianConsult)}</em> : null}
          </span>
          <span className="mdash-hbar-track">
            <span style={{ width: `${(row.count / max) * 100}%` }} />
          </span>
          <strong>{row.count}</strong>
        </li>
      ))}
    </ul>
  );
}

function KpiBar({
  label,
  hint,
  value,
  target,
}: {
  label: string;
  hint?: string;
  value: number | null;
  target: number;
}) {
  const over = value != null && value > target;
  const scale = Math.max(target * 1.45, value || 0, 1);
  return (
    <div className="mdash-kbar">
      <div className="mdash-kbar-label">
        <span>{label}</span>
        {hint ? <em>{hint}</em> : null}
      </div>
      <div className="mdash-kbar-track">
        {value != null ? (
          <span
            className={`mdash-kbar-fill${over ? ' mdash-kbar-fill--over' : ''}`}
            style={{ width: `${Math.min(100, (value / scale) * 100)}%` }}
          />
        ) : (
          <span className="mdash-kbar-fill mdash-kbar-fill--empty" />
        )}
        <i className="mdash-kbar-tick" style={{ left: `${(target / scale) * 100}%` }} />
      </div>
      <div className={`mdash-kbar-val${over ? ' mdash-kbar-val--over' : value != null ? ' mdash-kbar-val--ok' : ''}`}>
        <strong>{formatDurationCompact(value)}</strong>
        <em>target {formatDurationCompact(target)}</em>
      </div>
    </div>
  );
}

function EndToEndFlow({
  kpis,
}: {
  kpis: {
    pay: number | null;
    id: number | null;
    start: number | null;
    consult: number | null;
    checkout: number | null;
    e2e: number | null;
    ready: number | null;
  };
}) {
  const consultEnd =
    kpis.start != null && kpis.consult != null ? kpis.start + kpis.consult : null;
  const nodes = [
    { id: 'reg', label: 'Register', sec: 0, target: null as number | null },
    { id: 'pay', label: 'Payment', sec: kpis.pay, target: KPI.pay },
    { id: 'id', label: 'ID verified', sec: kpis.id, target: KPI.id },
    { id: 'ready', label: 'Pt ready', sec: kpis.ready, target: KPI.start },
    { id: 'start', label: 'Consult start', sec: kpis.start, target: KPI.start },
    { id: 'end', label: 'Consult end', sec: consultEnd, target: KPI.start + KPI.consult },
    { id: 'out', label: 'Checkout', sec: kpis.e2e, target: KPI.e2e },
  ];
  const startLeads = (kpis.start || 0) >= (kpis.consult || 0);
  const longest = startLeads
    ? { label: 'Register → Start of consultation', value: kpis.start, target: KPI.start }
    : { label: 'Start → End of consultation', value: kpis.consult, target: KPI.consult };
  const longPoleOver = longest.value != null && longest.value > longest.target;
  return (
    <div className="mdash-flow">
      <p className="mdash-flow-title">End-to-end flow · median timestamps</p>
      <ol>
        {nodes.map((n) => {
          const over = n.target != null && n.sec != null && n.sec > n.target;
          return (
            <li key={n.id} className={over ? 'is-over' : n.sec != null ? 'is-ok' : undefined}>
              <i />
              <span>{n.label}</span>
              <em>{n.id === 'reg' ? '00:00' : n.sec != null ? formatFlowOffset(n.sec) : '—'}</em>
            </li>
          );
        })}
      </ol>
      {longest.value != null ? (
        <p className={`mdash-flow-note${longPoleOver ? ' mdash-flow-note--over' : ''}`}>
          The long pole is <strong>{longest.label}</strong> ({formatDurationCompact(longest.value)})
          {startLeads
            ? ' — patients wait for a doctor pick far longer than the consult itself takes.'
            : '.'}
        </p>
      ) : (
        <p className="mdash-flow-note">Not enough completed visits to compute a median journey.</p>
      )}
    </div>
  );
}

function formatFlowOffset(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `+${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function ReasonSummaryDialog({
  kind,
  stats,
  typeRows,
  from,
  to,
  onClose,
}: {
  kind: 'cancel' | 'refund' | 'completed';
  stats: TeleconsultManagementDashboard;
  typeRows?: TeleconsultManagementTypeCount[];
  from: string;
  to: string;
  onClose: () => void;
}) {
  const isCancel = kind === 'cancel';
  const isRefund = kind === 'refund';
  const rows = isCancel ? stats.cancelReasons : isRefund ? stats.refundReasons : typeRows ?? stats.byType;
  const count = isCancel ? stats.cancelled : isRefund ? stats.refunded : stats.completed;
  const title = isCancel ? `Cancelled — ${count} visits` : isRefund ? `Refunded — ${count} visits` : `Completed — ${count} visits`;
  const share = pct(count, stats.total);
  const max = Math.max(1, ...rows.map((r) => r.count));
  const top = rows[0];
  return (
    <div className="staff-mgmt__dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="mdash-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mdash-reason-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <div>
            <h2 id="mdash-reason-title">{title}</h2>
            <p>
              {from === to ? formatChipRange(from, to) : formatChipRange(from, to)} · {share} of all visits
              {isCancel || isRefund ? ' · reasons as recorded by staff' : ' · by visit type'}
            </p>
          </div>
          <button type="button" className="mdash-modal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>
        {rows.length === 0 ? (
          <p className="mdash-empty">Nothing to summarise in this range.</p>
        ) : (
          <ul className="mdash-reason-list">
            {rows.map((row) => (
              <li key={row.type}>
                <div>
                  <span>{row.type}</span>
                  <strong>{row.count}</strong>
                </div>
                <span className={`mdash-reason-track${kind === 'completed' ? ' mdash-reason-track--ok' : ''}`}>
                  <span style={{ width: `${(row.count / max) * 100}%` }} />
                </span>
                <em>{pct(row.count, count)}</em>
              </li>
            ))}
          </ul>
        )}
        {top && (isCancel || isRefund) ? (
          <p className="mdash-modal-tip">
            Tip: “{top.type}” is the largest bucket
            {top.type.toLowerCase().includes('no reason') ? ' — consider making the reason field mandatory at cancel time.' : '.'}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function formatChipRange(from: string, to: string): string {
  const a = parseIso(from);
  const b = parseIso(to);
  const fromDt = new Date(a.y, a.m, a.d);
  const toDt = new Date(b.y, b.m, b.d);
  if (from === to) {
    return fromDt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  const sameMonth = a.y === b.y && a.m === b.m;
  const left = fromDt.toLocaleDateString('en-GB', { day: 'numeric', month: sameMonth ? undefined : 'short' });
  const right = toDt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${left}–${right}`;
}

function LogRow({
  visit,
  onAction,
  onOpenChat,
}: {
  visit: TeleconsultManagementVisit;
  onAction: (kind: ActionKind, visit: TeleconsultManagementVisit) => void;
  onOpenChat: (visit: TeleconsultManagementVisit) => void;
}) {
  const hops = buildJourneyHops(visit);
  const refundedAmt = visitRefundedSgd(visit);
  const reason =
    visit.status === 'cancelled'
      ? visit.cancellationReason
      : refundedAmt > 0 || visit.refunded
        ? visit.refundReason
        : '';
  const amount =
    visit.stripeAmountSgd != null
      ? refundedAmt > 0
        ? `${formatSgd(visit.stripeAmountSgd)} · ↩ ${formatSgd(refundedAmt)}`
        : formatSgd(visit.stripeAmountSgd)
      : '—';
  return (
    <tr>
      <td>
        <strong>{visit.patientName || '—'}</strong>
        <span className="mdash-nric">#{nricLast4(visit.maskedNric)}</span>
        {visit.hasChat && visit.chatMessageCount !== 0 ? (
          <button type="button" className="mdash-chat-link" onClick={() => onOpenChat(visit)}>
            Chat{visit.chatMessageCount != null ? ` (${visit.chatMessageCount})` : ''}
          </button>
        ) : null}
      </td>
      <td>
        <span>{staffKey(visit)}</span>
        <span className="mdash-consult-dr" title="Consult doctor">{doctorKey(visit)}</span>
        <span className="mdash-nric">{staffStepLabel(visit)}</span>
      </td>
      <td>
        <span>{visit.type || '—'}</span>
        <span className="mdash-amt">{amount}</span>
      </td>
      <td>
        <div className="mdash-status-cell">
          <span>{formatManagementDay(visit.registeredAt || visit.visitDate)}</span>
          <span className={`staff-mgmt__status staff-mgmt__status--${visit.status}`}>{statusLabel(visit.status)}</span>
        </div>
      </td>
      <td className="mdash-log-journey">
        <ol className="mdash-log-flow" aria-label="Visit progress">
          {hops.map((hop) => (
            <li key={hop.step.key} className={hop.className} title={hop.hint || undefined}>
              <em className={hop.overIn ? 'mdash-hop--over' : hop.hopIn != null ? 'mdash-hop--ok' : 'mdash-hop-spacer'}>
                {hop.hopIn != null ? formatSignedMinutes(hop.hopIn) : '\u00a0'}
              </em>
              <i />
              <span className={hop.done ? 'mdash-log-flow__time' : 'mdash-muted'}>
                {hop.done ? formatManagementClock(hop.at) : '—'}
              </span>
              <span className="mdash-log-flow__label">{hop.step.label}</span>
            </li>
          ))}
        </ol>
      </td>
      <td className="mdash-reason-cell">
        {reason ? (
          <span className={visit.status === 'cancelled' ? 'mdash-reason--bad' : 'mdash-reason--warn'}>
            {visit.status === 'cancelled' ? '× ' : '↩ '}
            {reason}
          </span>
        ) : (
          '—'
        )}
        {visit.canCancel || visit.canRefund ? (
          <span className="staff-mgmt__actions">
            {visit.canCancel ? (
              <button type="button" className="staff-mgmt__action" onClick={() => onAction('cancel', visit)}>
                Cancel
              </button>
            ) : null}
            {visit.canRefund ? (
              <button
                type="button"
                className="staff-mgmt__action staff-mgmt__action--warn"
                onClick={() => onAction('refund', visit)}
              >
                Refund
                {visitRemainingSgd(visit) > 0 && visitRemainingSgd(visit) < (visit.stripeAmountSgd ?? 0)
                  ? ` ${formatSgd(visitRemainingSgd(visit))}`
                  : ''}
              </button>
            ) : null}
          </span>
        ) : null}
      </td>
      <td>
        {visit.documentLabels && visit.documentLabels.length > 0 ? (
          <span className="mdash-docs">
            {visit.documentLabels.map((label) => (
              <span key={label} className="mdash-doc">
                {label}
              </span>
            ))}
          </span>
        ) : (
          <span className="mdash-muted">—</span>
        )}
      </td>
    </tr>
  );
}
